import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, SearchableSelect, useToast, v } from '@kss/ui';
import { Save, ArrowLeft } from 'lucide-react';
import type { Warehouse, WarehouseCreate } from '@kss/api';
import { warehouseService } from '@kss/api';
import { loadStoreOptions } from '../shared/formLoaders';
import AddressFields from '../../components/AddressFields';

/**
 * Warehouse form — fields rewritten 2026-05-02 to match the live API.
 * Address is split into the standard Indian-locale columns (live API
 * was silently dropping the `address` string we used to send).
 */

type FormState = {
  name: string;
  code: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  storeId: string;
  isActive: boolean;
};
const emptyForm: FormState = {
  name: '',
  code: '',
  addressLine1: '',
  city: '',
  state: '',
  postalCode: '',
  storeId: '',
  isActive: true,
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewWarehouse() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const isEdit = !!id && id !== 'new';

  const [form, setForm] = useState<FormState>(emptyForm);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /* Name was presence-only on the button; code was unchecked. Code uses
     the alphanumeric allowlist since it is a machine key ("WH-001"). */
  const errs: Record<string, string | null> = {
    name: v.runRules(form.name, [v.required("Name"), v.maxLength(160, "Name"), ...v.safeText("Name")]),
    code: v.runRules(form.code, [v.maxLength(40, "Code"), v.alphanumericChars("Code")]),
  };
  const fErr = (k: string) => (touched[k] ? errs[k] ?? undefined : undefined);
  const markF = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const formValid = !Object.values(errs).some(Boolean);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setErr(null);
        try {
          const r = await warehouseService.get(id);
          if (c) return;
          setForm({
            name: r.name ?? '',
            code: r.code ?? '',
            addressLine1: r.addressLine1 ?? r.address ?? '',
            city: r.city ?? '',
            state: r.state ?? '',
            postalCode: r.postalCode ?? '',
            storeId: r.storeId ?? '',
            isActive: !!r.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!c) setLoading(false);
        }
      }
    }
    run();
    return () => {
      c = true;
    };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setTouched({ name: true, code: true });
    const problem = Object.values(errs).find(Boolean);
    if (problem) {
      toast.error('Fix errors', problem);
      return;
    }
    setSaving(true);
    try {
      const body: Warehouse = {
        warehouseId: isEdit ? id : undefined,
        name: form.name,
        code: form.code || undefined,
        addressLine1: form.addressLine1 || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        storeId: form.storeId || undefined,
        isActive: form.isActive,
      };
      if (isEdit && id) {
        await warehouseService.update([body]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        const { warehouseId: _o, ...create } = body;
        await warehouseService.create([create as WarehouseCreate]);
        toast.success('Saved', `${form.name} added`);
      }
      navigate('/panel/warehouses');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/warehouses')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit warehouse' : 'New warehouse'}
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">
                Name <span className="text-danger">*</span>
              </span>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={markF("name")} placeholder="Main Warehouse" className={inputCls + (fErr("name") ? " border-danger" : "")} />
              {fErr("name") && <span className="text-xs text-danger">{fErr("name")}</span>}
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Code</span>
              <input value={form.code} onChange={(e) => set("code", e.target.value)} onBlur={markF("code")} placeholder="WH-001" className={inputCls + (fErr("code") ? " border-danger" : "")} />
              {fErr("code") && <span className="text-xs text-danger">{fErr("code")}</span>}
            </label>
            <SearchableSelect
              label="Store"
              value={form.storeId}
              onChange={(v) => set('storeId', v)}
              loadOptions={loadStoreOptions}
              placeholder="Select a store"
              searchPlaceholder="Search stores..."
            />
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Status</span>
              <select
                value={form.isActive ? 'active' : 'inactive'}
                onChange={(e) => set('isActive', e.target.value === 'active')}
                className={selectCls}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
            {/* Shared address picker — pincode auto-fills city/state.
                Country is intentionally omitted: live Warehouse table
                has no country column. */}
            <AddressFields
              addressLine1={form.addressLine1}
              setAddressLine1={(v) => set('addressLine1', v)}
              pincode={form.postalCode}
              setPincode={(v) => set('postalCode', v)}
              city={form.city}
              setCity={(v) => set('city', v)}
              state={form.state}
              setState={(v) => set('state', v)}
            />
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/warehouses')}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !formValid}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
